import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiConfig from "../../config/apiConfig";
import TnsLogo from "../../assets/images/tns-logo.svg";
import TestCardsTable from "./TestCardsTable";

export default function ParkingCheckoutLayout({
    paymentSession,
    sidebarContent,
    total = 0,
    containerId = 'hosted-checkout',
    headingText = 'Booking',
    marketingText = 'Yes, I would like to receive discounts, deals, news, announcements, and competitions. By ticking this box, I agree to be contacted by email and SMS.',
    currency = 'USD',
}) {
    const navigate = useNavigate();
    const currencySymbol = { USD: '$', AUD: 'A$', GBP: '£', EUR: '€', BRL: 'R$' }[currency] || '$';
    const [activeTab, setActiveTab] = useState('details');
    const [form, setForm] = useState({
        firstName: '', lastName: '', email: '', confirmEmail: '',
        postcode: '', mobile: '', licensePlate: '', register: false, marketing: false
    });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mobileView, setMobileView] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    useEffect(() => {
        if (!paymentSession) {
            setError('No valid payment session available.');
            setIsLoading(false);
            return;
        }
        const script = document.createElement('script');
        script.src = apiConfig.checkout.hostedCheckoutEndpoint;
        script.async = true;
        script.onload = () => {
            const HostedCheckoutSDK = window.HostedCheckoutSDK || window.exports?.HostedCheckoutSDK;
            if (!HostedCheckoutSDK) {
                setError('Failed to load payment SDK.');
                setIsLoading(false);
                return;
            }
            const hostedCheckoutSdk = new HostedCheckoutSDK();
            hostedCheckoutSdk.initialize({
                containerId,
                sessionId: paymentSession.sessionId,
                environment: 'uat',
                transactionRequestMode: 'PERFORM_TRANSACTION',
                locale: 'en_AU',
                onBackToCart: () => navigate('/shoppingcart'),
                onStartInitialization: () => setIsLoading(true),
                onEndInitialization: () => setIsLoading(false),
                onPaySubmit: () => { console.log('Payment submitted'); return Promise.resolve('SUCCESS'); },
                onApprove: (response) => {
                    navigate('/checkoutresult', { state: { transactionData: { ...response.data, successful: true, approvedAmount: response.data.approvedAmount || '0.00', totalAmount: response.data.totalAmount || '0.00' }, currency } });
                },
                onError: (err) => {
                    navigate('/checkoutresult', { state: { transactionData: { ...err.data, successful: false, approvedAmount: err.data?.approvedAmount || '0.00', totalAmount: err.data?.totalAmount || '0.00', status: err.data?.status || 'Transaction Failed' }, currency } });
                },
                onSessionError: () => { setError('Payment session expired.'); setIsLoading(false); },
                onGooglePayAuthorized: (response) => {
                    navigate('/checkoutresult', { state: { transactionData: { ...response.data, successful: true, approvedAmount: response.data.approvedAmount || '0.00', totalAmount: response.data.totalAmount || '0.00' }, currency } });
                },
                onGooglePayCanceled: () => {},
                onGooglePayError: () => { setError('Unable to process GooglePay.'); setIsLoading(false); },
                onPaypalCaptured: (response) => {
                    navigate('/checkoutresult', { state: { transactionData: { ...response.data, successful: true, approvedAmount: response.data.approvedAmount || '0.00', totalAmount: response.data.totalAmount || '0.00', paymentMethod: 'PayPal' }, currency } });
                },
                onPaypalCanceled: () => { setError('PayPal payment was canceled.'); setIsLoading(false); },
                onPaypalError: (err) => {
                    navigate('/checkoutresult', { state: { transactionData: { ...err.data, successful: false, approvedAmount: '0.00', totalAmount: err.data?.totalAmount || '0.00', status: 'PayPal Transaction Failed', paymentMethod: 'PayPal' }, currency } });
                },
                onApplePayAuthorized: (response) => {
                    navigate('/checkoutresult', { state: { transactionData: { ...response.data, successful: true, approvedAmount: response.data.approvedAmount || '0.00', totalAmount: response.data.totalAmount || '0.00', paymentMethod: 'Apple Pay' }, currency } });
                },
                onApplePayCanceled: () => { setError('Apple Pay was canceled.'); setIsLoading(false); },
                onApplePayError: () => { setError('Unable to process Apple Pay.'); setIsLoading(false); },
            });
            setTimeout(() => setIsLoading(false), 3000);
        };
        script.onerror = () => { setError('Failed to load payment form.'); setIsLoading(false); };
        document.head.appendChild(script);
    }, [paymentSession, navigate, containerId]);

    const renderInput = (label, name, type = 'text', placeholder) => (
        <div>
            <input
                type="text" name={name} value={form[name]} onChange={handleChange}
                placeholder={placeholder || `${label}`}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                aria-label={label}
                autoComplete="off"
            />
        </div>
    );

    const StepIndicator = ({ small }) => (
        <div className="flex items-center gap-0">
            {['Booking', 'Select', 'Pay'].map((step, i) => {
                const isLast = i === 2;
                const sz = small
                    ? (isLast ? 'w-6 h-6 sm:w-8 sm:h-8' : 'w-5 h-5 sm:w-6 sm:h-6')
                    : (isLast ? 'w-8 h-8' : 'w-6 h-6');
                const txtSz = small ? 'text-[9px] sm:text-[10px]' : 'text-[10px]';
                return (
                    <div key={step} className="flex items-center gap-0">
                        {i > 0 && <div className={`${small ? 'w-6 sm:w-8' : 'w-8'} h-0.5 bg-purple-400 -mt-3`}></div>}
                        <div className="flex flex-col items-center">
                            <div className={`${sz} rounded-full ${isLast ? 'bg-purple-700 border-2 border-purple-700 flex items-center justify-center' : 'bg-purple-200 border-2 border-purple-400'}`}>
                                {isLast && <div className={`${small ? 'w-1.5 h-1.5 sm:w-2 sm:h-2' : 'w-2 h-2'} rounded-full bg-white`}></div>}
                            </div>
                            <span className={`${txtSz} ${isLast ? 'font-semibold text-gray-700' : 'text-gray-500'} mt-1`}>{step}</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    const pageContent = (
        <div className={`min-h-screen bg-gray-50 ${mobileView ? 'px-4 pt-8 pb-4' : 'p-4'}`}>
            <div className={mobileView ? 'max-w-full' : 'max-w-5xl mx-auto'}>
                {/* Mobile header */}
                {mobileView && (
                    <div className="mb-6">
                        <div className="flex flex-col gap-3">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 leading-tight text-left">
                                    Confirm your
                                    <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent"> {headingText}</span>
                                </h1>
                                <p className="text-sm text-gray-600 mt-1 text-left">Enter your details or log in to finalise your booking.</p>
                            </div>
                            <div className="flex items-center gap-0 flex-shrink-0">
                                <p className="text-xs text-gray-500 mr-3 block">Step 3 of 3</p>
                                <StepIndicator small />
                            </div>
                        </div>
                    </div>
                )}

                <div className={`flex flex-col ${mobileView ? '' : 'lg:flex-row'} gap-6`}>
                    {/* Left sidebar */}
                    <div className={mobileView ? 'w-full' : 'lg:w-1/3'}>
                        <img src={TnsLogo} alt="TNS Logo" className="h-10 sm:h-12 mb-4" />
                        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 shadow-sm">
                            {sidebarContent}
                            <div className="border-t pt-4 space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Subtotal</span>
                                    <span className="font-semibold">{currencySymbol}{total.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Credit/debit card fee</span>
                                    <span className="text-gray-500 text-xs">to be applied</span>
                                </div>
                                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                                    <span>TOTAL</span>
                                    <span>{currencySymbol}{total.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right — form + payment */}
                    <div className={mobileView ? 'w-full' : 'lg:w-2/3'}>
                        {/* Desktop header */}
                        {!mobileView && (
                            <div className="mb-6">
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                                    <div>
                                        <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 leading-tight text-left">
                                            Confirm your
                                            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent"> {headingText}</span>
                                        </h1>
                                        <p className="text-sm sm:text-base text-gray-600 mt-1 text-left">Enter your details or log in to finalise your booking.</p>
                                    </div>
                                    <div className="flex items-center gap-0 flex-shrink-0">
                                        <p className="text-xs text-gray-500 mr-3 hidden sm:block">Step 3 of 3</p>
                                        <StepIndicator />
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex border-b border-gray-200 mb-4 sm:mb-6">
                            <button onClick={() => setActiveTab('details')}
                                className={`px-4 sm:px-6 py-2 sm:py-3 text-sm font-medium ${activeTab === 'details' ? 'border-b-2 border-gray-900 text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                                Enter details
                            </button>
                            <button onClick={() => setActiveTab('login')}
                                className={`px-4 sm:px-6 py-2 sm:py-3 text-sm font-medium ${activeTab === 'login' ? 'border-b-2 border-gray-900 text-gray-900 bg-gray-800 text-white rounded-t-lg' : 'text-gray-500 hover:text-gray-700 bg-gray-100 rounded-t-lg'}`}>
                                Log in
                            </button>
                        </div>

                        {activeTab === 'details' ? (
                            <div className="text-left">
                                <p className="text-sm text-gray-600 mb-6">
                                    Once you have entered your details registering is easy and will make booking even faster next time.
                                </p>
                                <div className="space-y-3 sm:space-y-4">
                                    {renderInput("First name", "firstName")}
                                    {renderInput("Last name", "lastName")}
                                    {renderInput("Email", "email", "email")}
                                    {renderInput("Confirm email", "confirmEmail", "email")}
                                    {renderInput("Postcode", "postcode")}
                                    {renderInput("Mobile phone number", "mobile", "tel")}
                                </div>

                                <div className="mt-6 sm:mt-8">
                                    <h3 className="font-bold text-gray-900 mb-2 text-left">Register</h3>
                                    <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                                        <input type="checkbox" name="register" checked={form.register} onChange={handleChange} className="mt-0.5" />
                                        I would like to register my details to make future bookings easier.
                                    </label>
                                </div>

                                <div className="mt-6 sm:mt-8">
                                    <h3 className="font-bold text-gray-900 mb-1 text-left">Vehicle licence plate number</h3>
                                    <p className="text-sm text-gray-600 mb-3 text-left">
                                        We scan your licence plate and match it to your booking for easy car park entry.
                                    </p>
                                    {renderInput("Licence plate no (e.g. 1ABC234)", "licensePlate", "text", "* Licence plate no (e.g. 1ABC234)")}
                                </div>

                                <div className="mt-6 sm:mt-8">
                                    <h3 className="font-bold text-gray-900 mb-0 text-left">Payment details</h3>
                                    {error && <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg mb-4">{error}</div>}
                                    {isLoading && (
                                        <div className="flex items-center justify-center py-8">
                                            <div className="inline-block w-5 h-5 border-2 border-purple-700 border-t-transparent rounded-full animate-spin mr-3"></div>
                                            <span className="text-sm text-gray-600">Loading secure payment form...</span>
                                        </div>
                                    )}
                                    <div id={containerId} className={isLoading ? 'opacity-0' : 'opacity-100 transition-opacity duration-300'}></div>
                                </div>

                                <div className="mt-6">
                                    <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
                                        <input type="checkbox" name="marketing" checked={form.marketing} onChange={handleChange} className="mt-0.5" />
                                        {marketingText}
                                    </label>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-12 text-gray-500">
                                <p>Log in functionality coming soon.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div>
            <div className="fixed top-20 right-4 z-50 flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1.5 shadow-sm">
                <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
                    <input type="checkbox" checked={mobileView} onChange={(e) => setMobileView(e.target.checked)} className="w-3 h-3" />
                    Mobile
                </label>
            </div>
            <div className={mobileView ? 'min-h-screen bg-gray-200 flex items-start justify-center pt-8 pb-8' : ''}>
                <div className={mobileView ? 'relative w-[390px] h-[844px]' : ''}>
                    {mobileView && (
                        <>
                            <div className="absolute inset-0 bg-black rounded-[3rem] shadow-2xl z-0"></div>
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-black rounded-b-2xl z-20"></div>
                        </>
                    )}
                    <div className={mobileView ? 'absolute inset-[12px] bg-white rounded-[2.4rem] overflow-y-auto overflow-x-hidden z-10' : ''}>
                        {pageContent}
                    </div>
                </div>
            </div>
            {!mobileView && (
                <div className="max-w-5xl mx-auto mt-8 pb-8">
                    <TestCardsTable />
                </div>
            )}
        </div>
    );
}
